'use client'

/**
 * Purpose: Survey face-analysis outcome screens (no face, multiple faces, rate limit, error).
 * Inputs: stage key, notConfigured flag, locale, optional retryAfter, callbacks.
 * Outputs: Retry or questionnaire fallback UI (retry hidden when rate-limited).
 */

import { useTranslations } from 'next-intl'
import HoverGradientButton from '@/components/buttons/HoverGradientButton/HoverGradientButton'

export type SurveyCameraOutcomeProps = {
  stage:
    | 'noFace'
    | 'multipleFaces'
    | 'imageBlurry'
    | 'imageUnderexposed'
    | 'unsafeContent'
    | 'rateLimited'
    | 'visionUnavailable'
    | 'error'
  notConfigured: boolean
  locale: string
  retryAfter: string | null
  onTryAgain: () => void
  onUseQuestionnaire: () => void
}

export function SurveyCameraOutcome({
  stage,
  notConfigured,
  locale,
  retryAfter,
  onTryAgain,
  onUseQuestionnaire,
}: SurveyCameraOutcomeProps) {
  const t = useTranslations('survey.photo')
  // Translation namespace keys align with stage: noFace | multipleFaces | rateLimited | error.
  const messageKey = stage
  const isRateLimited = stage === 'rateLimited'
  // Outage and rate-limit both push the questionnaire — hide retry to avoid another 503/429 loop.
  const hideRetry = isRateLimited || stage === 'visionUnavailable'

  // Format the quota-reset time in the viewer's locale when the API sent retryAfter.
  const description = notConfigured
    ? t('notConfigured')
    : isRateLimited
      ? t('rateLimited.description', {
          retryAt: retryAfter
            ? new Date(retryAfter).toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' })
            : '',
        })
      : t(`${messageKey}.description`)

  // Retry returns to capture; questionnaire fallback skips face analysis.
  // Rate-limited users only get the questionnaire CTA — retry would just 429 again.
  return (
    <div id="survey-camera-capture-outcome" className="survey-camera-capture-outcome">
      <h2>{notConfigured ? t('notConfiguredTitle') : t(`${messageKey}.title`)}</h2>
      <p className="survey-camera-capture-outcome-description">{description}</p>
      <div className="survey-camera-capture-outcome-actions">
        {!notConfigured && !hideRetry && (
          <HoverGradientButton id="survey-camera-outcome-retry" onClick={onTryAgain} className="cursor-pointer">
            {t('tryAgain')}
          </HoverGradientButton>
        )}
        <HoverGradientButton id="survey-camera-outcome-questionnaire" variant="outline" onClick={onUseQuestionnaire} className="cursor-pointer">
          {t('useQuestionnaire')}
        </HoverGradientButton>
      </div>
    </div>
  )
}
